import { useId, useState } from "react";

import { Button } from "@/components/ui/button";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { mensajeDe } from "@/lib/errors";

import { KIND_LABEL, PRIORITY_LABEL, useCreateImprovement, type ImprovementKind, type ImprovementPriority } from "./api";
import { reloj } from "./grabadora";

const megas = (bytes: number) => `${(bytes / 1024 / 1024).toLocaleString("es-CO", { maximumFractionDigits: 1 })} MB`;

/**
 * **La ficha de la grabación**: dos preguntas —«¿Qué es esto?» (el título) y «¿Qué quieres
 * cambiar?» (la nota)—, el tipo y la prioridad sugerida. La pantalla donde estaba y la persona las
 * pone el sistema (la ruta al empezar; quién, la sesión). «Descartar» es la única forma de perder la
 * grabación: cerrar el panel, recargar o cerrar el ERP la deja «sin guardar» (en el cajón del
 * navegador hasta que el servidor la tenga).
 */
export function FichaMejora({ archivo, segundos, ruta, maxBytes, onGuardada, onDescartar }: {
  archivo: Blob;
  segundos: number;
  ruta: string;
  maxBytes: number;
  onGuardada: (codigo: string | null) => void;
  onDescartar: () => void;
}) {
  const id = useId();
  const [kind, setKind] = useState<ImprovementKind | "">("");
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<ImprovementPriority>("medium");
  const [note, setNote] = useState("");
  const guardar = useCreateImprovement();
  const pesada = archivo.size > maxBytes;
  const lista = kind !== "" && title.trim().length >= 3 && !pesada && !guardar.isPending;

  async function enviar() {
    if (!lista) return;
    try {
      const creada = await guardar.mutateAsync({ ficha: { kind, title: title.trim(), priority, note, screen_path: ruta, duration_s: segundos }, archivo });
      onGuardada(creada?.code ?? null);
    } catch {
      // El motivo del servidor queda escrito abajo; la grabación sigue aquí para reintentar.
    }
  }

  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); void enviar(); }}>
      <p className="text-sm text-ink-2">
        {archivo.type.startsWith("audio/") ? "Solo voz" : "Pantalla y voz"} · {reloj(segundos)} · {megas(archivo.size)} · en <code>{ruta}</code>
      </p>
      {pesada ? <p role="alert" className="text-sm text-bad">La grabación pesa {megas(archivo.size)} y el tope es {megas(maxBytes)}: graba menos tiempo.</p> : null}
      <Field label="¿Qué es esto?" htmlFor={`${id}-titulo`} hint="Lo que encontraste, en una línea.">
        <Input id={`${id}-titulo`} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="¿Qué quieres cambiar?" htmlFor={`${id}-nota`} hint="Cómo debería quedar.">
        <Textarea id={`${id}-nota`} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Tipo" htmlFor={`${id}-tipo`}>
          <Select id={`${id}-tipo`} value={kind} onChange={(e) => setKind(e.target.value as ImprovementKind | "")}>
            <option value="" disabled>Elige…</option>
            {Object.entries(KIND_LABEL).map(([valor, nombre]) => <option key={valor} value={valor}>{nombre}</option>)}
          </Select>
        </Field>
        <Field label="Prioridad sugerida" htmlFor={`${id}-prioridad`}>
          <Select id={`${id}-prioridad`} value={priority} onChange={(e) => setPriority(e.target.value as ImprovementPriority)}>
            {Object.entries(PRIORITY_LABEL).map(([valor, nombre]) => <option key={valor} value={valor}>{nombre}</option>)}
          </Select>
        </Field>
      </div>
      {guardar.error ? <p role="alert" className="text-sm text-bad">{mensajeDe(guardar.error)}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!lista}>{guardar.isPending ? "Guardando…" : "Guardar"}</Button>
        <Button type="button" variant="secondary" disabled={guardar.isPending} onClick={onDescartar}>Descartar grabación</Button>
      </div>
    </form>
  );
}
